import React from 'react';
import { CheckCircle2, CircleSlash, X, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatDateTime } from '@/utils/format-date';

interface RunStatusPillProps {
  status: 'success' | 'failed' | 'stopped';
  timestamp: string | null;
  onClearRun: () => void;
  onDismiss: () => void;
  onViewDetails?: () => void;
}

const STATUS_CONTENT: Record<
  RunStatusPillProps['status'],
  { label: string; icon: React.ReactNode }
> = {
  success: {
    label: 'Run complete',
    icon: <CheckCircle2 className='size-4 shrink-0 text-green-500' />,
  },
  failed: {
    label: 'Run failed',
    icon: <XCircle className='size-4 shrink-0 text-red-500' />,
  },
  stopped: {
    label: 'Run stopped',
    icon: <CircleSlash className='size-4 shrink-0 text-muted-foreground' />,
  },
};

export const RunStatusPill: React.FC<RunStatusPillProps> = ({
  status,
  timestamp,
  onClearRun,
  onDismiss,
  onViewDetails,
}) => {
  const { label, icon } = STATUS_CONTENT[status];
  const showViewDetails = status !== 'success';

  return (
    <div className='flex items-center gap-1 rounded-lg border border-border bg-background/95 py-1 pl-3 pr-1 shadow-md backdrop-blur-sm'>
      <div className='flex items-center gap-2 pr-1'>
        {icon}
        <div className='flex flex-col leading-tight'>
          <span className='text-sm font-medium text-foreground'>{label}</span>
          {timestamp && (
            <span className='text-xs text-muted-foreground'>
              {formatDateTime(timestamp)}
            </span>
          )}
        </div>
      </div>

      {showViewDetails && onViewDetails && (
        <Button
          variant='ghost'
          size='sm'
          className='h-7 text-muted-foreground'
          onClick={onViewDetails}
        >
          View details
        </Button>
      )}

      <div className='mx-1 h-6 w-px bg-border' />

      <Button variant='ghost' size='sm' className='h-7' onClick={onClearRun}>
        Clear Run
      </Button>

      <Button
        variant='ghost'
        size='icon'
        className='size-7 shrink-0 text-muted-foreground'
        onClick={onDismiss}
        aria-label='Dismiss'
      >
        <X className='size-4' />
      </Button>
    </div>
  );
};
